import { useEffect, useRef } from "react";

const FADE_MS = 600;
const LEAD_S = 0.6;

/* Looping background video that fades in, fades out just before the end,
   then restarts — a soft loop instead of a hard cut. */
export const FadingVideo = ({ src, poster, className }) => {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let raf = null;
    let fadingOut = false;

    const fadeTo = (target) => {
      cancelAnimationFrame(raf);
      const from = parseFloat(video.style.opacity) || 0;
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min((now - t0) / FADE_MS, 1);
        video.style.opacity = String(from + (target - from) * t);
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const onLoaded = () => {
      video.play().catch(() => {});
      fadeTo(1);
    };
    const onTime = () => {
      const left = video.duration - video.currentTime;
      if (!fadingOut && video.duration && left > 0 && left <= LEAD_S) {
        fadingOut = true;
        fadeTo(0);
      }
    };
    const onEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => {});
      fadingOut = false;
      fadeTo(1);
    };

    video.addEventListener("loadeddata", onLoaded);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("ended", onEnded);
    if (video.readyState >= 2) onLoaded();
    return () => {
      cancelAnimationFrame(raf);
      video.removeEventListener("loadeddata", onLoaded);
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("ended", onEnded);
    };
  }, [src]);

  return (
    <video
      ref={ref}
      data-testid="background-video"
      className={className}
      style={{ opacity: 0 }}
      src={src}
      poster={poster}
      autoPlay
      muted
      playsInline
      preload="auto"
    />
  );
};
